import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatINR } from '../utils/formatters';
import { Broadcast } from '@phosphor-icons/react';

/**
 * Real-time watchlist ticker that streams via /api/ws/watchlist and falls back
 * to a marquee display when data arrives. Auto-reconnects on close.
 */
function buildWsUrl() {
  const backend = process.env.REACT_APP_BACKEND_URL || '';
  return backend.replace(/^http/, 'ws') + '/api/ws/watchlist';
}

export default function WatchlistTicker() {
  const [quotes, setQuotes] = useState([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => {
    let ws;
    let reconnectTimer;
    let mounted = true;

    const connect = () => {
      if (!mounted) return;
      try {
        ws = new WebSocket(buildWsUrl());
        wsRef.current = ws;
        ws.onopen = () => setConnected(true);
        ws.onmessage = (evt) => {
          try {
            const msg = JSON.parse(evt.data);
            if (Array.isArray(msg.quotes)) setQuotes(msg.quotes);
          } catch (err) {
            console.error('watchlist ws parse', err);
          }
        };
        ws.onclose = () => {
          setConnected(false);
          if (mounted) reconnectTimer = setTimeout(connect, 5000);
        };
        ws.onerror = () => setConnected(false);
      } catch (e) {
        console.error('watchlist ws connect failed', e);
      }
    };
    connect();
    return () => {
      mounted = false;
      if (ws && ws.readyState !== WebSocket.CONNECTING && ws.readyState !== WebSocket.CLOSED) {
        ws.close();
      }
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, []);

  if (quotes.length === 0) {
    return (
      <div className="border-y border-white/10 bg-[#0A0B0E] py-2 px-4 text-xs text-[#64748B]" data-testid="watchlist-ticker">
        <Broadcast size={12} className="inline mr-1.5 text-[#94A3B8]" />
        Connecting to live watchlist stream…
      </div>
    );
  }

  return (
    <div
      className="border-y border-white/10 bg-[#0A0B0E] overflow-hidden relative"
      data-testid="watchlist-ticker"
    >
      <div className="flex items-center gap-2 px-3 py-1.5 border-r border-white/10 absolute top-0 left-0 bottom-0 bg-[#0A0B0E] z-10">
        <Broadcast size={12} className={`${connected ? 'text-[#00FF66] animate-pulse' : 'text-[#94A3B8]'}`} />
        <span className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-mono whitespace-nowrap">
          Watchlist
        </span>
      </div>
      <div className="flex gap-8 py-2 pl-32 whitespace-nowrap animate-marquee">
        {/* Duplicate list so marquee loops seamlessly */}
        {[...quotes, ...quotes].map((q, i) => (
          <QuoteChip key={`${q.symbol}-${i}`} q={q} />
        ))}
      </div>
    </div>
  );
}

function QuoteChip({ q }) {
  const positive = (q.change_percent ?? 0) >= 0;
  const color = positive ? 'text-[#00FF66]' : 'text-[#FF3366]';
  const arrow = positive ? '▲' : '▼';
  return (
    <Link
      to={`/stock/${q.symbol}`}
      className="flex items-baseline gap-2 hover:bg-white/5 px-1"
      data-testid={`ticker-${q.symbol}`}
    >
      <span className="font-mono text-white text-sm">{q.symbol}</span>
      <span className="font-mono text-sm text-[#94A3B8]">{q.ltp ? formatINR(q.ltp) : '—'}</span>
      <span className={`text-xs font-mono ${color}`}>
        {arrow} {q.change_percent != null ? `${q.change_percent.toFixed(2)}%` : ''}
      </span>
    </Link>
  );
}
